import { ISubTopic } from '../store/SubTopicSlice';
import { ITopic } from '../store/TopicSlice';
import { ITenant } from '../store/V2/TenantsSlice';
import { getUrlConvertedString } from './Strings';
import { getAppBaseUrlForTenant } from './Tenant';

interface ISubjectStructuredData {
  curriculum: string;
  subject: string;
  subjectCode: string;
  appTenant?: ITenant;
}

interface ITopicStructuredData {
  curriculum: string;
  subject: string;
  subjectLevel: string;
  subjectCode: string;
  topic: string;
  appTenant?: ITenant;
}

interface ISubTopicStructuredData {
  topicData?: ITopic;
  subTopicData?: ISubTopic;
  appTenant?: ITenant;
}

export const getSubjectStructuredData = (
  data: ISubjectStructuredData
): Record<string, unknown> => {
  const { curriculum, subject, subjectCode, appTenant } = data;

  const baseUrl = `${getAppBaseUrlForTenant(appTenant)}${
    process.env.PUBLIC_URL
  }`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${curriculum} ${subject} Revision Resources`,
    description: `Our ${curriculum} ${subject} course (${subjectCode}) is tailored to meet the latest ${curriculum} standards. Our comprehensive module includes video lessons, interactive quizzes, worked examples, and downloadable worksheets.`,
    courseCode: subjectCode,
    educationalCredentialAwarded: curriculum,
    provider: {
      '@type': 'Organization',
      name: appTenant?.name || 'Tutopiya',
      url: baseUrl,
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: 'PT3H',
    },
    offers: {
      '@type': 'Offer',
      category: `${curriculum} ${subject} Course`,
    },
  };
};

export const getTopicStructuredData = (
  data: ITopicStructuredData
): Record<string, unknown> => {
  const {
    curriculum,
    subject,
    subjectLevel,
    subjectCode,
    topic,
    appTenant,
  } = data;

  const baseUrl = `${getAppBaseUrlForTenant(appTenant)}${
    process.env.PUBLIC_URL
  }`;

  const urlCurriculum = getUrlConvertedString(curriculum);
  const urlSubject = getUrlConvertedString(subject);
  const urlSubjectLevel = getUrlConvertedString(subjectLevel);
  const urlSubjectCode = getUrlConvertedString(subjectCode);

  const subjectUrl = `${baseUrl}/dashboard/${urlCurriculum}/${urlSubject}/${urlSubjectLevel}/${urlSubjectCode}`;

  const mainDescription = `Comprehensive learning resources and interactive lessons on ${topic} in the ${curriculum} ${subject} course. Master key concepts through video lessons, practice questions, and detailed explanations.`;
  const subjectDescription = `Complete ${curriculum} ${subject} course with comprehensive revision resources. Includes video lessons, practice questions, and detailed explanations for all topics.`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${topic} - ${curriculum} ${subject}`,
    description: mainDescription,
    courseCode: `${subjectCode}-${getUrlConvertedString(topic)}`,
    educationalCredentialAwarded: curriculum,
    educationalLevel: subjectLevel,
    inLanguage: 'en',
    provider: {
      '@type': 'Organization',
      name: appTenant?.name || 'Tutopiya',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/logo192.png`,
      },
    },
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: 'PT3H',
        offers: {
          '@type': 'Offer',
          category: `${curriculum} ${subject} Course`,
          availability: 'https://schema.org/InStock',
          price: '0',
          priceCurrency: 'USD',
          url: window.location.href,
        },
      },
    ],
    isPartOf: {
      '@type': 'Course',
      name: `${curriculum} ${subject} Revision Resources`,
      courseCode: subjectCode,
      url: subjectUrl,
      description: subjectDescription,
      provider: {
        '@type': 'Organization',
        name: appTenant?.name || 'Tutopiya',
        url: baseUrl,
      },
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: 'PT3H',
      },
      offers: {
        '@type': 'Offer',
        category: `${curriculum} ${subject} Course`,
      },
    },
    offers: {
      '@type': 'Offer',
      category: `${curriculum} ${subject} Course`,
      availability: 'https://schema.org/InStock',
      price: '0',
      priceCurrency: 'USD',
      url: window.location.href,
    },
    url: window.location.href,
  };
};

export const getSubTopicStructuredData = (
  data: ISubTopicStructuredData
): Record<string, unknown> => {
  const { topicData, subTopicData, appTenant } = data;
  const {
    curriculum = '',
    subject = '',
    subjectLevel = '',
    subjectCode = '',
    name: topic = '',
    _id: topicId = '',
  } = topicData || {};
  const { name: subTopic = '' } = subTopicData || {};

  const baseUrl = `${getAppBaseUrlForTenant(appTenant)}${
    process.env.PUBLIC_URL
  }`;

  const urlCurriculum = getUrlConvertedString(curriculum);
  const urlSubject = getUrlConvertedString(subject);
  const urlSubjectLevel = getUrlConvertedString(subjectLevel);
  const urlSubjectCode = getUrlConvertedString(subjectCode);
  const urlTopic = getUrlConvertedString(topic);

  const topicUrl = `${baseUrl}/dashboard/${urlCurriculum}/${urlSubject}/${urlSubjectLevel}/${urlSubjectCode}/${urlTopic}/${topicId}`;

  const subTopicDescription = `Detailed learning resources and interactive lessons on ${subTopic} in the ${curriculum} ${subject} course. Master this specific topic through video lessons, practice questions, and step-by-step explanations.`;
  const topicDescription = `Comprehensive learning resources and interactive lessons on ${topic} in the ${curriculum} ${subject} course. Master key concepts through video lessons, practice questions, and detailed explanations.`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${subTopic} - ${curriculum} ${subject}`,
    description: subTopicDescription,
    courseCode: `${subjectCode}-${getUrlConvertedString(
      topic
    )}-${getUrlConvertedString(subTopic)}`,
    educationalCredentialAwarded: curriculum,
    educationalLevel: subjectLevel,
    inLanguage: 'en',
    provider: {
      '@type': 'Organization',
      name: appTenant?.name || 'Tutopiya',
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: `${baseUrl}/logo192.png`,
      },
    },
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: 'PT3H',
        offers: {
          '@type': 'Offer',
          category: `${curriculum} ${subject} Course`,
          availability: 'https://schema.org/InStock',
          price: '0',
          priceCurrency: 'USD',
          url: window.location.href,
        },
      },
    ],
    isPartOf: {
      '@type': 'Course',
      name: `${topic} - ${curriculum} ${subject}`,
      description: topicDescription,
      courseCode: `${subjectCode}-${getUrlConvertedString(topic)}`,
      url: topicUrl,
      provider: {
        '@type': 'Organization',
        name: appTenant?.name || 'Tutopiya',
        url: baseUrl,
      },
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: 'PT3H',
      },
      offers: {
        '@type': 'Offer',
        category: `${curriculum} ${subject} Course`,
        availability: 'https://schema.org/InStock',
        price: '0',
        priceCurrency: 'USD',
        url: topicUrl,
      },
    },
    offers: {
      '@type': 'Offer',
      category: `${curriculum} ${subject} Course`,
      availability: 'https://schema.org/InStock',
      price: '0',
      priceCurrency: 'USD',
      url: window.location.href,
    },
    url: window.location.href,
  };
};
